import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';

import { Text, Icon } from '@pinpoint/design-kit';

import { formatNumber, formatPercent } from 'src/functions';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

export function CellDate({ value, ...props }) {
  return (
    <Text
      textWeight="400"
      textSize="small"
      data-tip={value ? dayjs(value).format('HH:mm [GMT]Z (zzz) — ddd, MMM D, YYYY') : null}
      {...props}
    >
      {value ? dayjs(value).format('MMM DD, YYYY') : '-'}
    </Text>
  );
}

CellDate.propTypes = {
  value: PropTypes.instanceOf(Date).isRequired,
};

export function CellPercent({ value, ...TextProps }) {
  return (
    <Text textWeight="400" textSize="small" {...TextProps}>
      {formatPercent(value)}
    </Text>
  );
}

CellPercent.propTypes = {
  value: PropTypes.number.isRequired,
};

export function CellText({ value, copy, ...TextProps }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      setTimeout(() => setCopied(false), 1000);
    }
  }, [copied]);

  let icon = null;
  if (copy) {
    icon = copied ? (
      <Icon
        name="Checked"
        color="success800"
        m={{ l: '0.5rem' }}
        size=".75rem"
        viewBox="0 0 50 50"
        data-tip="Copied!"
        cursor="none"
      />
    ) : (
      <Icon
        name="Copy"
        color="shadeCC"
        m={{ l: '0.5rem' }}
        size=".75rem"
        viewBox="0 0 14 14"
        onClick={() => {
          setCopied(true);
          navigator.clipboard.writeText(value);
        }}
        data-tip="Copy"
        cursor="copy"
      />
    );
  }

  return (
    <>
      <Text textWeight="400" textSize="small" {...TextProps}>
        {value}
      </Text>
      {icon}
    </>
  );
}

CellText.defaultProps = {
  copy: false,
};

CellText.propTypes = {
  value: PropTypes.string.isRequired,
  copy: PropTypes.bool,
};

export function CellNumber({ value, ...TextProps }) {
  return (
    <Text textWeight="400" textSize="small" {...TextProps}>
      {formatNumber(value)}
    </Text>
  );
}

CellNumber.propTypes = {
  value: PropTypes.number.isRequired,
};
